import axios from 'axios';

import { dpConfig } from 'src/config/dpConfig';
import { useBoundStore } from 'src/store/store';
import { userManager } from 'src/userManager';

async function getAccessToken(): Promise<string | null> {
  try {
    const currentUser = await userManager.getUser();
    return currentUser?.access_token || null;
  } catch {
    return null;
  }
}

const api = axios.create({
  baseURL: dpConfig.http.api,
  httpsAgent: dpConfig.env !== 'development',
});

api.interceptors.request.use(
  async (config) => {
    const token = await getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const loadingId = config.url || 'unknown-request';
    useBoundStore.getState().setLoading({ id: loadingId });

    return config;
  },
  (error) => {
    throw error;
  },
);

api.interceptors.response.use(
  (response) => {
    const loadingId = response.config.url || 'unknown-request';
    useBoundStore.getState().removeLoading(loadingId);
    return response;
  },
  async (error) => {
    const loadingId = error.config?.url || 'unknown-request';
    useBoundStore.getState().removeLoading(loadingId);

    if (error.response?.status === 401) {
      try {
        const newUser = await userManager.signinSilent();
        if (newUser?.access_token) {
          error.config.headers.Authorization = `Bearer ${newUser.access_token}`;
          return await api.request(error.config);
        }
      } catch (err: unknown) {
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          await userManager.signoutRedirect();
        }
      }
    }

    return Promise.reject(error);
  },
);

export default api;
